import React, { useState, useEffect } from "react";
import Select from "react-select";
import "react-datepicker/dist/react-datepicker.css";
import { useDispatch, useSelector } from "react-redux";
import { useAuthUserDetails } from "components/Common/getAuthUserDetails";
import { Row, Col, Card, Button, CardBody, Container, Label, FormGroup, Form, Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap'
import { useFormik, ErrorMessage } from "formik";
import { fetchCategory, fetchDepartment, fetchEmployeeDropdownAutoList, fetchEmployeeIncrementReport, clearapproveError } from "store/actions";
import Breadcrumbs from "components/Common/Breadcrumb";
import TableContainer from "components/Common/TableContainer";
import CustomDatePicker from "common/CustomDatePicker";
import * as Yup from "yup";
import { FaCheck } from "react-icons/fa";
import { Input } from "reactstrap";
import Swal from "sweetalert2";
import Header from "components/VerticalLayout/Header";
import { exportEmployeeToExcel } from "pages/exports/exportExcel";
import { exportEmployeeToPDF } from "pages/exports/exportPDF";

const IncrementReport = () => {
  const [authDetails, setAuthDetails] = useState(null);
  const dispatch = useDispatch();
  const {
    categoryList,
    departmentList,
    dropdownAutoList,
    employeeIncrementReport,
    loading,
    success,
    error,
  } = useSelector((state) => ({
    categoryList: state.EmployeeDetailsReducer?.categoryList ?? [],
    departmentList: state.EmployeeDetailsReducer?.departmentList ?? [],
    dropdownAutoList: state.EmployeeDetailsReducer?.dropdownAutoList ?? [],
    employeeIncrementReport: state.PayrollSalaryEmployeeSalaryDefinitionReducer?.employeeIncrementReport ?? [],
    loading: state.PayrollSalaryEmployeeSalaryDefinitionReducer?.loading,
    error: state.PayrollSalaryEmployeeSalaryDefinitionReducer?.error,
    success: state.PayrollSalaryEmployeeSalaryDefinitionReducer?.success,

  }));

  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const toggle = () => setDropdownOpen((prev) => !prev);

  useEffect(() => {
    const authUserDetails = useAuthUserDetails();
    setAuthDetails(authUserDetails[0])
    const unitId = authUserDetails[0].unit_id
    dispatch(fetchDepartment(unitId));
    dispatch(fetchCategory(unitId));
    dispatch(fetchEmployeeDropdownAutoList(unitId));
  }, [dispatch]);

  const formatDate = (date) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const formik = useFormik({
    initialValues: {
      category: "",
      department: "",
      fromDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
      toDate: new Date(),
      employeeIncrementReport: [],
    },
    validationSchema: Yup.object({
      fromDate: Yup.date().nullable().required("From Date is required"),
      toDate: Yup.date().nullable().required("To Date is required").min(Yup.ref("fromDate"), "To Date must be after From Date"),
    }),
    onSubmit: (values, { resetForm }) => {
      const formData = new FormData();
      formData.append("cat_id", selectedCategory?.value || 0);
      formData.append("dept_id", selectedDepartment?.value || 0);
      formData.append("emp_id", selectedEmployee?.value || 0);
      formData.append("unit_id", authDetails?.unit_id);
      formData.append("from_dt", formatDate(values.fromDate));
      formData.append("to_dt", formatDate(values.toDate));
      formData.append("create_by", authDetails?.user_id);

      dispatch(fetchEmployeeIncrementReport(formData));
    }
  });

  const columns = [
    { Header: "S.No", Cell: ({ row }) => employeeIncrementReport.length - row.index, className: "serial-no", applyHeaderClass: true, },
    { Header: "Employee Code", accessor: "emp_no" },
    { Header: "Employee Name", accessor: "emp_name" },
    { Header: "Increment Date", accessor: "increment_date" },
    { Header: "Increment Type", accessor: "increment_type" },
    { Header: "BASIC", accessor: "BASIC" },
    { Header: "DA", accessor: "DA" },
    { Header: "HRA", accessor: "HRA" },
    { Header: "CONV", accessor: "CONV" },
    { Header: "Increment Amount", accessor: "increment_amount" },
    { Header: "Gross", accessor: "gross_salary" },
    { Header: "Revision", accessor: "revision" },
  ];

  useEffect(() => {
    return () => {
      dispatch(clearapproveError());
    };
  }, []);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Payroll / Increment" breadcrumbItem="Increment Report" />
          <Row>
            <Col lg="12">
              <Card>
                <CardBody>
                  <Form onSubmit={formik.handleSubmit}>
                    <div className="row mb-3">
                      <div className="col-md-3">
                        <Label>Category</Label>
                        <Select
                          id="category"
                          name="category"
                          options={categoryList.map((dept) => ({
                            value: dept.empCatgryid,
                            label: dept.empCategory,
                          }))}
                          value={selectedCategory}
                          onChange={(selected) => setSelectedCategory(selected)}
                          isClearable
                          className="hrms-select"
                          classNamePrefix="hrms-select"
                        />
                      </div>
                      <div className="col-md-3">
                        <Label>Department</Label>
                        <Select
                          id="department"
                          name="department"
                          options={departmentList.map((dept) => ({
                            value: dept.departmentId,
                            label: dept.departmentname,
                          }))}
                          value={selectedDepartment}
                          onChange={(selected) => setSelectedDepartment(selected)}
                          isClearable
                          className="hrms-select"
                          classNamePrefix="hrms-select"
                        />
                      </div>
                      <div className="col-md-3">
                        <FormGroup>
                          <Label>From Date <span className="text-danger">*</span></Label>
                          <CustomDatePicker
                            name="fromDate"
                            value={formik.values.fromDate}
                            onChange={(date) => { formik.setFieldValue("fromDate", date); }}
                            onBlur={formik.handleBlur}
                          />
                          {formik.touched.fromDate && formik.errors.fromDate && (
                            <div className="text-danger">{formik.errors.fromDate}</div>
                          )}
                        </FormGroup>
                      </div>
                      <div className="col-md-3">
                        <FormGroup>
                          <Label>To Date <span className="text-danger">*</span></Label>
                          <CustomDatePicker
                            name="toDate"
                            value={formik.values.toDate}
                            onChange={(date) => { formik.setFieldValue("toDate", date); }}
                            onBlur={formik.handleBlur}
                          />
                          {formik.touched.toDate && formik.errors.toDate && (
                            <div className="text-danger">{formik.errors.toDate}</div>
                          )}
                        </FormGroup>
                      </div>
                    </div>
                    <div className="row mb-3">
                      <div className="col-md-4">
                        <Label>Employee</Label>
                        <Select
                          id="employee"
                          name="employee"
                          options={dropdownAutoList.map(emp => ({ value: emp.emp_id.toString(), label: emp.emp_no }))}
                          value={selectedEmployee}
                          onChange={(selected) => setSelectedEmployee(selected)}
                          className="hrms-select"
                          classNamePrefix="hrms-select"
                        />
                      </div>
                      <div className="col-md-8 d-flex justify-content-end align-items-center gap-2">
                        <Button type="submit" className="btn button-search me-2 mt-3" >
                          <i className="fas fa-search me-1" /> Search
                        </Button>
                        {employeeIncrementReport && employeeIncrementReport.length > 0 && (
                          <Dropdown isOpen={dropdownOpen} toggle={toggle} direction="down">
                            <DropdownToggle color="primary" className="btn button-export mt-3 me-2">
                              <i className="fas fa-file-export me-1" /> Export
                            </DropdownToggle>
                            <DropdownMenu>
                              <DropdownItem onClick={() => exportEmployeeToExcel(employeeIncrementReport, authDetails?.unit_fullname, authDetails?.unit_address, "Employee Increment Report")}>
                                <i className="fas fa-file-excel me-2 text-success" /> Export to Excel
                              </DropdownItem>
                              <DropdownItem onClick={() => exportEmployeeToPDF(employeeIncrementReport, authDetails?.unit_fullname, authDetails?.unit_address, "Employee Increment Report", columns)}>
                                <i className="fas fa-file-pdf me-2 text-danger" /> Export to PDF
                              </DropdownItem>
                            </DropdownMenu>
                          </Dropdown>
                        )}
                      </div>
                    </div>
                  </Form>
                </CardBody>
              </Card>
              <Card>
                <CardBody>
                  <TableContainer columns={columns} data={employeeIncrementReport || []} isGlobalFilter={true} customPageSize={10} className="custom-header-css" />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment >
  );
};

export default IncrementReport;
